import {
  Box,
  Header,
  SpaceBetween,
  Cards,
  Link,
  ColumnLayout,
  Pagination,
  CollectionPreferences,
  CollectionPreferencesProps,
  Table,
  Button,
  Modal,
  DateRangePickerProps,
  TableProps,
  ExpandableSection,
} from '@cloudscape-design/components';

import { useNavigate } from '@tanstack/react-router';
import { getContactMeta, getContacts } from '../../common/api/contacts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useUrlFilterState, useUrlRangeState } from '../../common/filtering/filters';
import { useEffect, useMemo, useState } from 'react';
import { useLocalStorage } from 'react-use';
import { PropertyDateFilter } from '../../common/filtering/date-filtering';
import { buildFiltering } from '../../common/filtering/contacts';
import { ResourceError } from '../../components/Loading';
import { exportExcel } from '../../common/excel';
import { PropertyFilterOption, PropertyFilterQuery } from '@cloudscape-design/collection-hooks';
import { PlaceContact } from '../../common/types';
import { formatDateString } from '../../common/formatting/dateFormat';
import { formatPhoneNumber } from '../../common/formatting/phoneFormat';
import { PhoneNumber } from '../PhoneNumber';
import { Email } from '../Email';
import { formatName } from '../../common/names';
import { TextQueryFilter } from '../TextQueryFilter';
import { StarsIcon } from '../StarsIcon';

export type ContactTableProps = {
  now: Date;
  setNow: (now: Date) => void;
  query: PropertyFilterQuery;
  setQuery?: (query: PropertyFilterQuery) => void;
  range?: DateRangePickerProps.Value | null;
  setRange?: (range: DateRangePickerProps.Value | null) => void;
  sortingField?: string;
  setSortingField?: (field: string | undefined) => void;
  sortDirection?: 'asc' | 'desc';
  setSortDirection?: (direction: 'asc' | 'desc') => void;
  hideActions?: boolean;
  hideFilter?: boolean;
  hidePagination?: boolean;
  hideColumns?: string[];
  variant?: 'embedded' | 'borderless' | 'stacked' | 'full-page';
  additionalActions?: React.ReactNode;
  selectedItems?: PlaceContact[];
  setSelectedItems?: (items: PlaceContact[]) => void;
  textQuery?: string;
  setTextQuery?: (text: string) => void;
};

export function ContactTable(props: ContactTableProps) {
  const {
    now,
    setNow,
    query,
    setQuery,
    range,
    setRange,
    sortingField,
    setSortingField,
    sortDirection,
    setSortDirection,
    selectedItems,
    setSelectedItems,
    textQuery,
    setTextQuery,
  } = props;
  const navigate = useNavigate();
  const resourceName = 'Google Review Reply Contacts';
  const endpointName = 'contacts';
  // relative times are in the past vs future
  const past = true;

  // TODO move to context
  const DEFAULT_PREFERENCES = {
    pageSize: 50,
  };
  const PAGE_SIZE_OPTIONS = [
    { value: 10, label: `10 ${resourceName}` },
    { value: 20, label: `20 ${resourceName}` },
    { value: 50, label: `50 ${resourceName}` },
    { value: 100, label: `100 ${resourceName}` },
  ];

  const [preferences, setPreferences] = useLocalStorage<
    CollectionPreferencesProps.Preferences<unknown>
  >('React-Cards-Preferences', DEFAULT_PREFERENCES);

  const pageSize = preferences?.pageSize || DEFAULT_PREFERENCES.pageSize;

  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);

  // const queryClient = useQueryClient();
  const metaQuery = useQuery({
    queryKey: [endpointName, 'meta'],
    queryFn: getContactMeta,
    staleTime: 360_000, // 10 minutes
  });

  const { options, properties } = useMemo(() => {
    if (metaQuery.data) {
      return {
        options: metaQuery.data.filteringOptions as PropertyFilterOption[],
        properties: metaQuery.data.filteringProperties,
      };
    }
    return {
      options: [],
      properties: [],
    };
  }, [metaQuery.data]);

  // wait for the meta query to finish before proceeding
  const { isPending, isFetching, error, data } = useQuery({
    queryKey: [
      endpointName,
      query,
      range,
      pageSize,
      sortingField,
      sortDirection,
      page,
      now,
      textQuery,
    ],
    enabled: metaQuery.isSuccess,
    // TODO add sorting
    queryFn: async () =>
      await getContacts(query, page, pageSize, range, sortingField, sortDirection, now, textQuery),
    // 1 minute
    staleTime: 60_000,
    // placeholderData: keepPreviousData,
  });

  const loading =
    (isPending || isFetching || metaQuery.isPending || metaQuery.isFetching) &&
    !error &&
    !metaQuery.error;

  useEffect(() => {
    if (data && data.pages !== pageCount) {
      setPageCount(data.pages);
    }
  }, [data]);

  const refresh = () => {
    setNow(new Date());
    metaQuery.refetch();
  };

  const pagination = (
    <Pagination
      currentPageIndex={page}
      disabled={loading}
      pagesCount={pageCount}
      onChange={({ detail }) => setPage(detail.currentPageIndex)}
    />
  );

  return (
    <Table
      onSortingChange={({ detail }) => {
        setSortingField?.(detail.sortingColumn.sortingField);
        setSortDirection?.(detail.isDescending ? 'desc' : 'asc');
      }}
      sortingColumn={{
        sortingField,
      }}
      // default to ascending
      sortingDescending={sortDirection === 'desc'}
      header={
        <Header
          variant={
            !props.variant
              ? 'awsui-h1-sticky'
              : props.variant !== 'full-page'
                ? 'h2'
                : 'awsui-h1-sticky'
          }
          description="View discovered contacts."
          actions={
            props.hideActions ? undefined : (
              <SpaceBetween size="xs" direction="horizontal">
                {props.additionalActions}
                <Button variant="normal" iconName="refresh" onClick={refresh} disabled={loading} />
                <Button
                  disabled={loading}
                  iconName="download"
                  onClick={() => {
                    exportExcel(
                      (data?.items ?? []).map((item) => ({
                        first_name: item.first_name,
                        last_name: item.last_name,
                        phone_number: item.phone_number,
                        email: item.email,
                        title: item.title,
                        department: item.department,
                        date: item.date,
                        place_name: item.place.name,
                        mentions: item.review_contacts.length,
                      })),
                      'contacts',
                    );
                  }}
                />
              </SpaceBetween>
            )
          }
          counter={
            data?.total && metaQuery.data?.total
              ? `(${data.total.toLocaleString()}/${metaQuery.data.total.toLocaleString()})`
              : undefined
          }
        >
          {resourceName}
        </Header>
      }
      selectedItems={selectedItems}
      onSelectionChange={
        setSelectedItems ? ({ detail }) => setSelectedItems(detail.selectedItems) : undefined
      }
      selectionType={setSelectedItems ? 'multi' : undefined}
      items={data?.items ?? []}
      loading={loading}
      loadingText={`Loading ${resourceName}...`}
      pagination={props.hidePagination ? undefined : pagination}
      preferences={
        <CollectionPreferences
          disabled={loading}
          pageSizePreference={{
            options: PAGE_SIZE_OPTIONS,
          }}
          preferences={preferences}
          onConfirm={(e) => setPreferences(e.detail)}
        />
      }
      filter={
        props.hideFilter ? undefined : (
          <SpaceBetween direction="vertical" size="xxxs">
            <TextQueryFilter
              loading={loading}
              text={textQuery ?? ''}
              setText={(q) => {
                setTextQuery?.(q);
                // reset page when filtering
                setPage(1);
              }}
            />
            <ExpandableSection headerText="Additional filters">
              <PropertyDateFilter
                loading={loading}
                query={query}
                setQuery={(q) => {
                  setQuery?.(q);
                  // reset page when filtering
                  setPage(1);
                }}
                range={range ?? null}
                setRange={(r) => {
                  setRange?.(r);
                  // reset page when filtering
                  setPage(1);
                }}
                propertyFilteringOptions={options}
                filteringProperties={properties}
                past={past}
              />
            </ExpandableSection>
          </SpaceBetween>
        )
      }
      variant={props.variant ?? 'full-page'}
      stickyHeader={!props.variant || props.variant === 'full-page'}
      empty={
        <Box color="inherit" margin={{ vertical: 'xs' }} textAlign="center">
          {error || metaQuery.error ? (
            <ResourceError
              error={error ?? metaQuery.error ?? new Error('Unknown error')}
              refetch={refresh}
              resourceName={resourceName}
            />
          ) : (
            <SpaceBetween size="xxs">
              <div>
                <b>No {resourceName.toLowerCase()}</b>
                <Box color="inherit" variant="p">
                  No {resourceName.toLowerCase()} found.
                </Box>
              </div>
            </SpaceBetween>
          )}
        </Box>
      }
      columnDefinitions={[
        {
          id: 'name',
          header: 'Name',
          cell: (item: PlaceContact) => (
            <SpaceBetween direction="vertical" size="xxxs">
              <Link
                href={`/dashboard/contacts/${item.uuid}`}
                variant="secondary"
                target="_blank"
                // onFollow={(e) => {
                //   e.preventDefault();
                //   navigate(`/dashboard/contacts/${item.uuid}`);
                // }}
              >
                {formatName(item.first_name, item.last_name)}
              </Link>
              <Box variant="span">
                {item.title ?? '-'}
                {item.department && (
                  <Box variant="span" color="text-body-secondary">
                    {` - ${item.department}`}
                  </Box>
                )}
              </Box>
            </SpaceBetween>
          ),
          sortingField: 'first_name',
        },
        {
          id: 'place',
          header: 'Place',
          minWidth: 220,
          cell: (item: PlaceContact) => (
            <Header
              variant="h2"
              description={
                <SpaceBetween direction="vertical" size="xxxs">
                  {item.place.address}
                  <SpaceBetween direction="horizontal" size="xs">
                    {item.place.rating?.toFixed(1) ?? '?.?'}
                    <StarsIcon stars={item.place.rating ?? undefined} />
                    <Link href={item.place.cid_url ?? undefined} target="_blank" variant="secondary">
                      {item.place.total_review_count?.toLocaleString() ?? '?'} Google reviews
                    </Link>
                  </SpaceBetween>
                </SpaceBetween>
              }
            >
              <Link
                href={`/dashboard/places/${item.place.uuid}`}
                variant="secondary"
                target="_blank"
                // onFollow={(e) => {
                //   e.preventDefault();
                //   navigate(`/dashboard/places/${item.uuid}`);
                // }}
              >
                {item.place.name}
              </Link>
            </Header>
          ),
        },
        {
          id: 'contact_info',
          header: 'Contact Info',
          cell: (item: PlaceContact) => (
            <SpaceBetween direction="vertical" size="xxxs">
              <PhoneNumber
                phoneNumber={item.phone_number}
                lineTypeInfo={item.phone_line_type_info}
                showLineTypeInfo
              />
              <Email email={item.email} />
            </SpaceBetween>
          ),
        },
        {
          id: 'date',
          header: 'Last Mentioned',
          cell: (item: PlaceContact) => (
            <SpaceBetween direction="vertical" size="xxxs">
              {item.date ? formatDateString(item.date) : '-'}
              <Link href={`/dashboard/contacts/${item.uuid}`} variant="secondary" target="_blank">
                {item.review_contacts.length} mentions
              </Link>
            </SpaceBetween>
          ),
          sortingField: 'date',
        },
        // {
        //   id: 'created',
        //   header: 'Created',
        //   cell: (item: PlaceContact) => formatDateString(item.created_at),
        // },
      ].filter((column) => !props.hideColumns?.includes(column.id))}
    />
  );
}
